/**
 * One card in the segmented student grid (Students tab).
 * Per spec §5.2 — fields are subject-scoped.
 *
 * Layout:
 *   [tag pill if any]
 *   [avatar] Name (Class N · engagement)         [last_active]
 *   ----------------------------------------------------
 *     ACCURACY   |   CONSISTENCY   |   QUESTIONS
 *     N% ↑ +X%   |   N/7 days       |   N
 *   ----------------------------------------------------
 *   [gap dots] N thinking gaps  [severity pill]
 *
 * Click → opens drill-down via the onOpen callback.
 */

const SUBJECT_AVATAR_PALETTE = [
  ['bg-teacher-purple-light', 'text-teacher-purple'],
  ['bg-teacher-blue-light',   'text-teacher-blue'],
  ['bg-teacher-green-light',  'text-teacher-green'],
  ['bg-teacher-amber-light',  'text-teacher-amber'],
  ['bg-pink-100',             'text-teacher-pink'],
];

function avatarStyleFor(studentId) {
  // Stable hash-of-id → palette index
  let h = 0;
  for (let i = 0; i < (studentId || '').length; i++) h = ((h << 5) - h + studentId.charCodeAt(i)) | 0;
  return SUBJECT_AVATAR_PALETTE[Math.abs(h) % SUBJECT_AVATAR_PALETTE.length];
}

function initials(name) {
  return (name || 'S')
    .split(' ').filter(Boolean).slice(0, 2)
    .map(w => w[0].toUpperCase()).join('') || 'S';
}

function lastActiveColor(label) {
  if (label === 'today') return 'text-teacher-green';
  if (label === 'yesterday') return 'text-teacher-amber';
  if (typeof label === 'string' && label.includes('day')) {
    const days = parseInt(label, 10);
    if (days >= 5) return 'text-teacher-red';
    if (days >= 3) return 'text-teacher-amber';
  }
  return 'text-teacher-text-tertiary';
}

function trendArrow(direction) {
  if (direction === 'up') return <span className="text-teacher-green">↑</span>;
  if (direction === 'down') return <span className="text-teacher-red">↓</span>;
  return <span className="text-teacher-text-tertiary">→</span>;
}

function trendDeltaText(deltaPct) {
  const sign = deltaPct > 0 ? '+' : deltaPct < 0 ? '' : '';
  return deltaPct === 0 ? '0%' : `${sign}${deltaPct}%`;
}

function tagPill(tag) {
  if (!tag) return null;
  const map = {
    rising:    { label: '↑ Rising',           cls: 'bg-pink-50 text-pink-700 border-pink-200' },
    promise:   { label: '→ Showing promise',  cls: 'bg-teacher-purple-light text-teacher-purple border-teacher-purple/30' },
    declining: { label: '↓ Declining',        cls: 'bg-teacher-red-light text-teacher-red border-teacher-red/30' },
  };
  const t = map[tag];
  if (!t) return null;
  return (
    <div className={`absolute top-2.5 right-2.5 text-[9px] px-2 py-0.5 rounded-full border font-medium ${t.cls}`}>
      {t.label}
    </div>
  );
}

function severityPill(severity) {
  if (!severity) return null;
  const map = {
    multiple_gaps_active: { label: 'Multiple gaps active', cls: 'bg-teacher-red-light text-teacher-red' },
    repeating_same_error: { label: 'Repeating same error', cls: 'bg-teacher-amber-light text-teacher-amber' },
  };
  const s = map[severity];
  if (!s) return null;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${s.cls}`}>{s.label}</span>
  );
}

function gapDot(status) {
  if (status === 'cleared') return 'bg-teacher-green';
  if (status === 'capsule_in_progress') return 'bg-teacher-amber';
  return 'bg-teacher-red';
}

export default function StudentCard({ student, onOpen }) {
  const [bg, fg] = avatarStyleFor(student.student_id);
  const acc = student.accuracy_pct || 0;
  const trend = student.accuracy_trend || { direction: 'flat', delta_pct: 0 };
  const consistency = student.consistency_days_out_of_7 || 0;
  const questions = student.questions_last_7d || 0;
  const gaps = student.gaps || [];

  return (
    <button
      onClick={() => onOpen?.(student.student_id)}
      className="bg-white border border-teacher-border rounded-xl p-3.5 text-left transition-all hover:border-teacher-purple-mid hover:shadow-[0_2px_12px_rgba(107,70,193,0.08)] relative w-full"
    >
      {tagPill(student.tag)}

      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-medium shrink-0 ${bg} ${fg}`}>
          {initials(student.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium leading-tight truncate">{student.name}</div>
          <div className="text-[11px] text-teacher-text-secondary truncate">
            {student.class ? `Class ${student.class}` : ''}
            {student.engagement_pattern ? ` · ${student.engagement_pattern}` : ''}
          </div>
        </div>
        <div className={`text-[10px] whitespace-nowrap ${lastActiveColor(student.last_active)}`}>
          {student.last_active}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-2.5">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-teacher-text-tertiary mb-0.5">Accuracy</div>
          <div className="text-[13px] font-medium">
            {acc}%
            {trendArrow(trend.direction)}
            <span className="text-[10px] ml-0.5 text-teacher-text-secondary">
              {trendDeltaText(trend.delta_pct || 0)}
            </span>
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-teacher-text-tertiary mb-0.5">Consistency</div>
          <div className="text-[13px] font-medium">{consistency}/7 days</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-teacher-text-tertiary mb-0.5">Questions</div>
          <div className="text-[13px] font-medium">{questions}</div>
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {gaps.length === 0 ? (
          <span className="text-[10px] text-teacher-text-tertiary">No gaps detected</span>
        ) : (
          <>
            <div className="flex gap-[3px]">
              {gaps.map((g, i) => (
                <div key={`${g.atlas_node_id}-${i}`} className={`w-[7px] h-[7px] rounded-full ${gapDot(g.status)}`} />
              ))}
            </div>
            <span className="text-[10px] text-teacher-text-tertiary">
              {gaps.length} thinking gap{gaps.length === 1 ? '' : 's'}
            </span>
            {severityPill(student.severity_label)}
          </>
        )}
      </div>
    </button>
  );
}
